import css from '@styled-system/css';
import React, { memo, SVGAttributes, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { theme as UITheme } from '../../theme';
import AnimatedCircle from '../../utils/animated-circle';
import rem from '../../utils/rem';
import { Box, BoxProps, Flex, Text } from '../primitives';
import AnimatedCounter from './AnimatedCounter';

export type ScoreProps = Omit<BoxProps, 'color' | 'size'> & {
  size: number;
  value?: number;
  isAnimated?: boolean;
  showLabel?: boolean;
  showCounterScore?: boolean;
};

const BASE_SIZE = 148;

const ratio = (size: number, base: number) => (size * base) / BASE_SIZE;

export const Score = memo(
  ({
    value = 0,
    size,
    isAnimated = false,
    showLabel = true,
    showCounterScore,
    ...rest
  }: ScoreProps) => {
    const { t } = useTranslation();

    const isLarge = size >= BASE_SIZE;

    const outlineStrokeWidth = ratio(size, 4);
    const progressStrokeWidth = outlineStrokeWidth * 2;
    const radius = size / 2 - outlineStrokeWidth;

    const getCirconference = useCallback(() => radius * 2 * Math.PI, [radius]);
    const getStrokeDashOffset = useCallback(
      ({ value }: { value?: number }) =>
        value ? (1 - value / 100) * getCirconference() : getCirconference(),
      [getCirconference]
    );

    const circonferenceStroke = getCirconference();
    const strokeDashoffset = getStrokeDashOffset({ value });

    const progressProps: SVGAttributes<SVGCircleElement> = {
      stroke: 'url(#progressGradient)',
      strokeLinecap: 'round',
      strokeWidth: rem(progressStrokeWidth),
      fill: '#ffffff00',
      r: rem(radius),
      cx: rem(size / 2),
      cy: rem(size / 2),
      strokeDasharray: circonferenceStroke,
    };

    return (
      <Box bg="light" borderRadius={rem(size)} size={rem(size)} {...rest}>
        <Box position="absolute" top={0} left={0} bottom={0} right={0}>
          <SSvg width={rem(size)} height={rem(size)}>
            <linearGradient
              id="progressGradient"
              x1="1"
              y1="1"
              x2="1"
              y2="0"
              gradientTransform="rotate(-115)"
            >
              <stop offset="0%" stopColor={UITheme.colors.gradientEnd}></stop>
              <stop
                offset="100%"
                stopColor={UITheme.colors.gradientStart}
              ></stop>
            </linearGradient>

            <SOutlineCircle
              strokeWidth={rem(outlineStrokeWidth)}
              fill="#ffffff00"
              r={rem(radius)}
              cx={rem(size / 2)}
              cy={rem(size / 2)}
            />
            {isAnimated ? (
              <AnimatedCircle
                {...progressProps}
                initialStrokeDashoffset={circonferenceStroke}
                finalStrokeDashOffset={strokeDashoffset}
              />
            ) : (
              <circle {...progressProps} strokeDashoffset={strokeDashoffset} />
            )}
          </SSvg>
        </Box>

        <Flex
          size={rem(size)}
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
        >
          <Text
            fontSize={rem(ratio(size, 64))}
            lineHeight={rem(isLarge ? ratio(size, 52) : size)}
            fontWeight="600"
            letterSpacing={rem(ratio(size, -3))}
            ml={isLarge ? 'small' : 0}
            mt={isLarge && showLabel ? 'small' : 0}
          >
            {showCounterScore ? <AnimatedCounter value={value} /> : value}
            <Text as="sup" fontWeight="normal" fontSize={rem(ratio(size, 28))}>
              %
            </Text>
          </Text>
          {isLarge && showLabel && (
            <Text fontSize={rem(ratio(size, 18))}>{t('score.label')}</Text>
          )}
        </Flex>
      </Box>
    );
  }
);

const SSvg = styled.svg`
  transform: rotate(-90deg);
  transform-origin: 50% 50%;
`;

const SOutlineCircle = styled.circle`
  ${css({
    stroke: 'gallery',
  }) as any}
`;
